import { HttpClient, type HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TokenService } from '@auth';
import { type Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { LOYALTY_CONFIG } from '../config.token';
import type { Member, MemberLite, MemberRewardPurchaseRequest, RewardDefinition } from '../types';
import { getOktaId } from '../utils/get-okta-id';

import { MemberCacheService } from './member-cache.service';

export const PURCHASE_REWARD_COUNTER_NAME = 'Visits';

@Injectable({ providedIn: 'root' })
export class LoyaltyService {
  private readonly http = inject(HttpClient);
  private readonly cfg = inject(LOYALTY_CONFIG);
  private readonly token = inject(TokenService);
  private readonly cache = inject(MemberCacheService);

  getMember(): Observable<Member> {
    const oktaId = getOktaId(this.token.getToken());

    return this.http
      .get<Member>(`${this.cfg.baseUrl}/v3/members`, {
        params: {
          oktaId,
          expands: 'counterDetails,rewards',
          'filter[counterDetails.status]': 'active',
          'filter[rewards.status]': 'active,redeemed',
        },
      })
      .pipe(
        tap((m) => this.cache.set(m)),
        catchError(this.handleError),
      );
  }

  getMemberLite(oktaId: string): Observable<MemberLite> {
    const safeOktaId = encodeURIComponent(oktaId);

    //Change URL in WEB-2673
    return this.http
      .get<MemberLite>(`${this.cfg.baseUrl}/v1/member/${safeOktaId}/lite`)
      .pipe(catchError(this.handleError));
  }

  getRewards(): Observable<RewardDefinition[]> {
    const oktaId = this.getOktaIdFromCache();

    return this.http
      .get<RewardDefinition[]>(`${this.cfg.baseUrl}/v3/rewards`, { params: { oktaId } })
      .pipe(catchError(this.handleError));
  }

  purchaseReward(rewardParentId: string): Observable<void> {
    const oktaId = this.getOktaIdFromCache();

    const body: MemberRewardPurchaseRequest = {
      member: { oktaId },
      rewardParentId,
      counterName: PURCHASE_REWARD_COUNTER_NAME,
    };

    return this.http
      .post<void>(`${this.cfg.baseUrl}/v3/member/reward/purchase`, body)
      .pipe(catchError(this.handleError));
  }

  private getOktaIdFromCache(): string {
    try {
      const member = this.cache.snapshot();

      return member.oktaId ?? getOktaId(this.token.getToken());
    } catch {
      return getOktaId(this.token.getToken());
    }
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    // TODO: replace with Sentry capture once instrumented
    console.error('Loyalty request failed', error);

    return throwError(() => new Error(`Loyalty request failed: ${error.status}`));
  }
}
